/**
 * Вкладка «Доступ»: ключи доски для чатов (выпуск — ключ показывается один раз, отзыв), как подключить ключ в
 * claude.ai/code (заголовок X-Board-Key), кому открыта доска (владелец портала добавляет и убирает людей; убрать —
 * значит и отозвать ключи человека) и перенос с прежней доски. Почту людей доски видит только владелец портала.
 */
import type { BoardImportResult, BoardKey, BoardKeyCreated, BoardMe, BoardMember } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Copy, KeyRound } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { dateTime, plural, relative } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { BOARD_API } from './access';
import { useRefreshBoard } from './shared';

/** Как подключить ключ к чатам: прокси claude.ai/code подставляет его сам, сессия чата ключа не видит */
export function KeyInstruction({ issued = false }: { issued?: boolean }) {
  const code = 'font-mono text-[13px]';
  return (
    <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[14px] leading-5 text-ink">
      <li>
        {issued
          ? 'Скопируйте ключ сейчас: портал показывает его один раз.'
          : 'Выпустите ключ в блоке «Ключи для чатов» и сразу скопируйте его: портал показывает ключ один раз.'}
      </li>
      <li>
        В claude.ai/code откройте окружение, в котором работают чаты EPC → настройки окружения → «Network
        secrets» → «Add secret».
      </li>
      <li>
        Allowed websites (Host): <code className={code}>app.epcpilot.ai</code>; заголовок:{' '}
        <code className={code}>X-Board-Key</code>; префикс — пусто; значение — ключ.
      </li>
      <li>
        Если форма предлагает только «Token» (это <code className={code}>Authorization: Bearer</code>), так
        тоже работает, но тогда вход в портал по cookie из этого окружения перестанет работать: портал примет
        заголовок Authorization за вход.
      </li>
      <li>
        Сохраните. Прокси сам добавит ключ к запросам на портал — сессия чата ключа не видит и не может его
        напечатать.
      </li>
      <li>
        Чат работает с доской командой <code className={code}>node scripts/board.mjs --chat plan …</code>: код
        чата — в каждой команде. <code className={code}>BOARD_CHAT</code> в переменные окружения
        claude.ai/code не кладите — все чаты окружения получат одно имя. Изменения по ключу без кода чата
        портал не принимает.
      </li>
      <li>
        Ключ работает от вашего имени: код чата бережёт от ошибок, но не от чужого процесса, у которого есть
        ключ. Ключ потерялся или попал не туда — отзовите его здесь и выпустите новый.
      </li>
    </ol>
  );
}

function CreatedKeyDialog({ created, onClose }: { created: BoardKeyCreated | null; onClose: () => void }) {
  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      toast('Ключ скопирован');
    } catch {
      toast('Не удалось скопировать: выделите ключ и скопируйте вручную', 'danger');
    }
  };
  return (
    <Dialog
      open={Boolean(created)}
      onOpenChange={(v) => !v && onClose()}
      width="lg"
      title={`Ключ «${created?.name ?? ''}» выпущен`}
      description="Ключ показан один раз: после закрытия окна увидеть его снова нельзя — только выпустить новый."
      footer={
        <>
          <Button variant="secondary" icon={<Copy className="size-4" />} onClick={copy}>
            Скопировать ключ
          </Button>
          <Button variant="primary" onClick={onClose}>
            Готово, ключ сохранён
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <code className="block rounded-md border border-line-strong bg-surface-2 px-3 py-3 font-mono text-[13px] break-all text-ink select-all">
          {created?.key}
        </code>
        <KeyInstruction issued />
      </div>
    </Dialog>
  );
}

function KeysPanel({ me }: { me: BoardMe }) {
  const refresh = useRefreshBoard();
  const keys = useQuery({
    queryKey: ['devboard', 'keys'],
    queryFn: () => api<BoardKey[]>(`${BOARD_API}/keys`),
  });
  const [name, setName] = useState('');
  const [created, setCreated] = useState<BoardKeyCreated | null>(null);
  const [revoking, setRevoking] = useState<BoardKey | null>(null);
  const create = useMutation({
    mutationFn: () => api<BoardKeyCreated>(`${BOARD_API}/keys`, { body: { name: name.trim() } }),
    onSuccess: (k) => {
      setCreated(k);
      setName('');
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<BoardKey>(`${BOARD_API}/keys/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      setRevoking(null);
      toast('Ключ отозван: чаты с ним больше не войдут');
      refresh();
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не получилось', 'danger'),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) create.mutate();
  }
  return (
    <Panel title={me.isOwner ? 'Ключи для чатов (все)' : 'Мои ключи для чатов'} bodyClassName="p-0">
      <form
        onSubmit={submit}
        className="flex flex-col gap-3 border-b border-line px-4 py-4 sm:flex-row sm:items-end md:px-5"
        noValidate
      >
        <Field
          label="Название ключа"
          htmlFor="key-name"
          hint="Где будет работать: «Окружение EPC в claude.ai/code»"
          className="min-w-0 flex-1"
        >
          <Input id="key-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button
          type="submit"
          icon={<KeyRound className="size-4" />}
          loading={create.isPending}
          disabled={!name.trim()}
        >
          Выпустить ключ
        </Button>
      </form>
      {create.error ? (
        <div className="px-4 pt-3 md:px-5">
          <ErrorBox error={create.error} />
        </div>
      ) : null}
      {keys.isPending ? (
        <div className="px-5">
          <Spinner />
        </div>
      ) : keys.isError ? (
        <div className="p-5">
          <ErrorBox error={keys.error} />
        </div>
      ) : !keys.data.length ? (
        <p className="px-4 py-4 text-[14px] text-ink-2 md:px-5">Ключей пока нет.</p>
      ) : (
        <ul className="divide-y divide-line">
          {keys.data.map((k) => (
            <li
              key={k.id}
              className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 md:px-5"
            >
              <div className="min-w-0 flex-1 text-[14px]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium [overflow-wrap:anywhere] text-ink">{k.name}</span>
                  <code className="font-mono text-[12px] text-ink-2">{k.prefix}…</code>
                  {k.revokedAt ? (
                    <Badge tone="danger">отозван</Badge>
                  ) : (
                    <Badge tone="success">действует</Badge>
                  )}
                </div>
                <div className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                  {me.isOwner ? `${k.userName} · ` : ''}выпущен {dateTime(k.createdAt)} · работал{' '}
                  {k.lastUsedAt ? relative(k.lastUsedAt) : 'ещё не работал'}
                  {k.revokedAt ? ` · отозван ${dateTime(k.revokedAt)}` : ''}
                </div>
              </div>
              {!k.revokedAt ? (
                <Button size="sm" variant="danger" onClick={() => setRevoking(k)}>
                  Отозвать
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <CreatedKeyDialog created={created} onClose={() => setCreated(null)} />
      <Dialog
        open={Boolean(revoking)}
        onOpenChange={(v) => !v && setRevoking(null)}
        width="sm"
        title={`Отозвать ключ «${revoking?.name ?? ''}»?`}
        description="Чаты, которые работают с этим ключом, сразу перестанут попадать на доску."
        footer={
          <>
            <Button variant="ghost" onClick={() => setRevoking(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() => revoking && revoke.mutate(revoking.id)}
            >
              Отозвать
            </Button>
          </>
        }
      >
        <p className="text-[14px] text-ink-2">Вернуть отозванный ключ нельзя — только выпустить новый.</p>
      </Dialog>
    </Panel>
  );
}

function MembersPanel({ me }: { me: BoardMe }) {
  const refresh = useRefreshBoard();
  const members = useQuery({
    queryKey: ['devboard', 'members'],
    queryFn: () => api<BoardMember[]>(`${BOARD_API}/members`),
  });
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState<BoardMember | null>(null);
  const add = useMutation({
    mutationFn: () => api<BoardMember>(`${BOARD_API}/members`, { body: { email: email.trim() } }),
    onSuccess: (m) => {
      toast(`${m.fullName}: доска открыта`);
      setEmail('');
      refresh();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api(`${BOARD_API}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRemoving(null);
      toast('Доска закрыта, ключи человека отозваны');
      refresh();
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не получилось', 'danger'),
  });
  return (
    <Panel
      title="Кому открыта доска"
      meta={members.data ? `${members.data.length}` : undefined}
      bodyClassName="p-0"
    >
      {members.isPending ? (
        <div className="px-5">
          <Spinner />
        </div>
      ) : members.isError ? (
        <div className="p-5">
          <ErrorBox error={members.error} />
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {members.data.map((m) => (
            <li
              key={m.userId}
              className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 md:px-5"
            >
              <div className="min-w-0 flex-1 text-[14px]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink">{m.fullName}</span>
                  {m.isOwner ? <Badge tone="accent">владелец портала</Badge> : null}
                  {m.userId === me.userId ? <Badge>это вы</Badge> : null}
                  {!m.isActive ? <Badge tone="danger">вход заблокирован</Badge> : null}
                </div>
                {m.email || m.addedAt ? (
                  <div className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {[
                      m.email,
                      m.addedAt
                        ? `с ${dateTime(m.addedAt)}${m.addedByName ? `, открыл(а) ${m.addedByName}` : ''}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                ) : null}
              </div>
              {me.isOwner && !m.isOwner ? (
                <Button size="sm" variant="ghost" onClick={() => setRemoving(m)}>
                  Закрыть доступ
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {me.isOwner ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErrors({});
            if (email.trim()) add.mutate();
          }}
          className="flex flex-col gap-3 border-t border-line px-4 py-4 sm:flex-row sm:items-end md:px-5"
          noValidate
        >
          <Field
            label="Почта учётной записи"
            htmlFor="member-email"
            hint="Человек уже должен входить в портал хотя бы в одной компании"
            error={errors.email}
            className="min-w-0 flex-1"
          >
            <Input
              id="member-email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              invalid={Boolean(errors.email)}
            />
          </Field>
          <Button type="submit" loading={add.isPending} disabled={!email.trim()}>
            Открыть доску
          </Button>
        </form>
      ) : null}
      {me.isOwner && add.error && !Object.keys(errors).length ? (
        <div className="px-4 pb-4 md:px-5">
          <ErrorBox error={add.error} />
        </div>
      ) : null}
      <Dialog
        open={Boolean(removing)}
        onOpenChange={(v) => !v && setRemoving(null)}
        width="sm"
        title={`Закрыть доску: ${removing?.fullName ?? ''}?`}
        description="Страница доски у человека закроется, все его ключи для чатов будут отозваны. Взятые им задачи останутся за ним — отпустите их, если нужно."
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => removing && remove.mutate(removing.userId)}
            >
              Закрыть доступ
            </Button>
          </>
        }
      >
        <p className="text-[14px] text-ink-2">
          Открыть снова можно в любой момент — по почте. Отозванные ключи не вернутся: для своих чатов человек
          выпустит новый ключ.
        </p>
      </Dialog>
    </Panel>
  );
}

function ImportPanel() {
  const refresh = useRefreshBoard();
  const input = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const run = useMutation({
    mutationFn: async (file: File) => {
      let body: unknown;
      try {
        body = JSON.parse(await file.text());
      } catch {
        throw new Error('Файл не читается как JSON: нужна выгрузка прежней доски (tasks, notes, chats)');
      }
      return api<BoardImportResult>(`${BOARD_API}/import`, { body });
    },
    onSuccess: () => refresh(),
  });
  const r = run.data;
  return (
    <Panel title="Перенос с прежней доски">
      <div className="flex flex-col gap-3 text-[14px]">
        <p className="text-ink-2">
          Файл JSON с коллекциями tasks, notes и chats прежней доски (артефакт claude.ai). Повторный перенос
          дублей не даёт: задачи и комментарии сверяются по номеру прежней доски, свежие отметки чатов не
          затираются.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={input}
            id="board-import"
            type="file"
            accept=".json,application/json"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setFileName(f.name);
                run.mutate(f);
              }
              e.target.value = '';
            }}
          />
          <Button loading={run.isPending} onClick={() => input.current?.click()}>
            Выбрать файл и перенести
          </Button>
          {fileName ? (
            <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">{fileName}</span>
          ) : null}
        </div>
        <ErrorBox error={run.error} />
        {r ? (
          <div role="status" className="rounded-md bg-success-soft px-3 py-2 text-[13px] text-ink">
            Перенесено: {plural(r.tasks, 'задача', 'задачи', 'задач')} (новых {r.created.tasks}),{' '}
            {plural(r.notes, 'комментарий', 'комментария', 'комментариев')} (новых {r.created.notes}),{' '}
            {plural(r.chats, 'строка чата', 'строки чатов', 'строк чатов')} (новых {r.created.chats});
            пропущено {r.skipped}.
            {r.problems.length ? (
              <ul className="mt-1 list-disc pl-5 text-ink-2">
                {[...new Set(r.problems)].map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

export function AccessTab({ me }: { me: BoardMe }) {
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="flex min-w-0 flex-col gap-5">
        <KeysPanel me={me} />
        <MembersPanel me={me} />
      </div>
      <div className="flex min-w-0 flex-col gap-5">
        <Panel title="Как подключить чаты">
          <KeyInstruction />
        </Panel>
        {me.isOwner ? <ImportPanel /> : null}
      </div>
    </div>
  );
}
